import {
  ComponentModal,
  CustomSize,
  DialogButtons,
  DialogCustomButton,
  DialogInputModal,
  DialogModal,
  DialogResults,
  FormModal,
  FormModalArgs,
  ModalArgs,
  ModalRef,
  ModalResult,
  ViewModal,
  ViewModalArgs,
} from '@msdk/common'
import React, { useMemo } from 'react'

import { DialogPopup, ModalForm, ModalView, UICustomization } from '../components'
import { I18nWrapper } from '../components/I18nWrapper'
import { ModalFederationWrapper } from '../components/ModalFederationWrapper'
import { ModalSuspenseWrapper } from '../components/ModalSuspenseWrapper/ModalSuspenseWrapper'
import { useModalContext } from '../providers'

export function useViewModal(): ViewModal {
  const modalContext = useModalContext()
  return useMemo(() => {
    return {
      createModal: (
        entityName: string,
        viewName: string,
        onDimiss: (args?: ModalResult) => void | Promise<void>,
        args?: ViewModalArgs,
      ) => {
        const modalId = `${entityName}.${viewName}`
        const modalBody = (
          <ModalSuspenseWrapper>
            <ModalFederationWrapper entityName={entityName} moduleName={viewName}>
              <I18nWrapper entityName={entityName} moduleName={viewName}>
                <ModalView
                  entityName={entityName}
                  viewName={viewName}
                  lookup={!!args?.lookup}
                  multiSelect={!!args?.multiSelect}
                  selectedRowIds={args?.selectedRows ?? []}
                  prefilledFilters={args?.prefilledFilters}
                />
              </I18nWrapper>
            </ModalFederationWrapper>
          </ModalSuspenseWrapper>
        )
        const modalRef = modalContext.add(modalId, modalBody, onDimiss, undefined, args) as ModalRef
        return modalRef
      },
      getModal: (entityName: string, viewName: string) => {
        const modalId = `${entityName}.${viewName}`
        return modalContext.get(modalId) as ModalRef
      },
    }
  }, [modalContext])
}

export function useFormModal(): FormModal {
  const modalContext = useModalContext()
  return useMemo(() => {
    return {
      createModal: (
        entityName: string,
        formName: string,
        id: string,
        onDimiss: (args?: ModalResult) => void | Promise<void>,
        args?: FormModalArgs,
      ) => {
        const modalId = `${entityName}.${formName}.${id}`
        const modalRef = modalContext.add(
          modalId,
          <ModalSuspenseWrapper>
            <ModalFederationWrapper entityName={entityName} moduleName={formName}>
              <I18nWrapper entityName={entityName} moduleName={formName}>
                <ModalForm
                  entityName={entityName}
                  formName={formName}
                  id={id}
                  mode={args?.mode ?? 'Edit'}
                  prefilledFields={args?.prefilledFields}
                  containerEntity={args?.containerEntity}
                />
              </I18nWrapper>
            </ModalFederationWrapper>
          </ModalSuspenseWrapper>,
          onDimiss,
          args?.modalForm?.size,
          args,
        ) as ModalRef
        return modalRef
      },
      getModal: (entityName: string, formName: string, id: string) => {
        const modalId = `${entityName}.${formName}.${id}`
        return modalContext.get(modalId) as ModalRef
      },
    }
  }, [modalContext])
}

export function useComponentModal(): ComponentModal {
  const modalContext = useModalContext()
  return useMemo(() => {
    return {
      createModal: (body: JSX.Element, onDimiss: (args?: ModalResult) => void | Promise<void>, args?: ModalArgs) => {
        const modalId = crypto.randomUUID()
        const modalRef = modalContext.add(modalId, body, onDimiss, undefined, args) as ModalRef
        return modalRef
      },
      getModal: (id: string) => {
        return modalContext.get(id) as ModalRef
      },
    }
  }, [modalContext])
}

export function useDialogModal(): DialogModal {
  const modalContext = useModalContext()
  return useMemo(() => {
    return {
      createModal: (
        title: string,
        message: string,
        buttons: DialogButtons,
        customButtons?: DialogCustomButton[],
        customSize?: CustomSize,
        uiCustomization?: UICustomization,
      ) => {
        return new Promise((resolve) => {
          const modalId = crypto.randomUUID()
          const onDismiss = (res?: DialogResults | string) => {
            resolve(res as DialogResults)
          }
          const body = (
            <DialogPopup
              title={title}
              message={message}
              buttons={buttons}
              onDimiss={onDismiss}
              customButtons={customButtons}
              customSize={customSize}
              uiCustomization={uiCustomization}
            />
          )
          const modalRef = modalContext.add(modalId, body, onDismiss) as ModalRef
          modalRef.open()
        })
      },
    }
  }, [modalContext])
}

export function useDialogInputModal(): DialogInputModal {
  const modalContext = useModalContext()
  return useMemo(() => {
    return {
      createModal: (
        title: string,
        message: string,
        buttons?: DialogButtons,
        customButtons?: DialogCustomButton[],
        mandatoryInput?: boolean,
      ) => {
        return new Promise((resolve) => {
          const modalId = crypto.randomUUID()
          const onDismiss = (res?: DialogResults | string) => {
            resolve(res as string)
          }
          const body = (
            <DialogPopup
              title={title}
              message={message}
              showInput
              onDimiss={onDismiss}
              buttons={buttons}
              customButtons={customButtons}
              mandatoryInput={mandatoryInput}
            />
          )
          const modalRef = modalContext.add(modalId, body, onDismiss) as ModalRef
          modalRef.open()
        })
      },
    }
  }, [modalContext])
}
